import React, { useRef, useState, useCallback, useMemo } from "react";
import Tag from "./Tag";
import {
  FileRowContainer,
  FileRowContent,
  FileRowLabel,
  FileRowStatus,
  FileRowLink,
  FileRowHelper,
  FileRowHelperIcon,
  FileRowHelperText,
  FileRowHelperBold,
  FileRowButton,
  FileRowInput,
} from "./primitives";

function FileUploadRow({
  fileConfig,
  isUploaded,
  filePath,
  fileName,
  isUploading,
  onUpload,
  depositAmount,
  setReloadFiles
}) {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploading]);

  const handleContainerClick = useCallback(() => {
    if (!isUploading && !isUploaded && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploading, isUploaded]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(fileConfig, file, setReloadFiles);
      e.target.value = "";
    }
  }, [fileConfig, onUpload]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the container (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer?.files;
    if (files?.length > 0 && !isUploading) {
      onUpload(fileConfig, files[0], setReloadFiles);
    }
  }, [fileConfig, isUploading, onUpload]);

  const helperContent = useMemo(() => {
    // Check if this is a Proof of Payment file (file_id 12) and depositAmount is provided
    const isProofOfPayment = fileConfig.fileId === 12;
    if (isProofOfPayment && depositAmount) {
      return {
        text: "To complete your re-registration, please ensure you pay the minimum deposit of ",
        boldText: depositAmount,
      };
    }
    
    if (fileConfig.helperText && !depositAmount) {
      return fileConfig.helperText;
    }
    
    return null;
  }, [fileConfig.helperText, fileConfig.fileId, fileConfig.id, fileConfig.label, depositAmount]);

  const displayFileName = useMemo(() => 
    fileName || (filePath ? filePath.split('/').pop() : null),
    [fileName, filePath]
  );

  return (
    <FileRowContainer
      $isUploaded={isUploaded}
      $isDragOver={isDragOver}
      $clickable={!isUploading}
      onClick={handleContainerClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <FileRowContent>
        <FileRowLabel>
          {fileConfig.label} {fileConfig.required ? "*" : ""}
        </FileRowLabel>

        {isUploaded ? (
          <FileRowStatus>
            <Tag variant="success">Uploaded</Tag>
            {displayFileName && (
              <FileRowLink
                href={filePath || "#"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fal fa-file"></i>
                {displayFileName}
              </FileRowLink>
            )}
          </FileRowStatus>
        ) : (
          <>
            {helperContent && (
              <FileRowHelper>
                <FileRowHelperIcon className="fas fa-exclamation-circle" />
                <FileRowHelperText>
                  {typeof helperContent === "string" ? (
                    helperContent
                  ) : (
                    <>
                      {helperContent.text}
                      <FileRowHelperBold>{helperContent.boldText}</FileRowHelperBold>.
                    </>
                  )}
                </FileRowHelperText>
              </FileRowHelper>
            )}
          </>
        )}
      </FileRowContent>

      <FileRowButton
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
      >
        {isUploading ? (
          <>
            <i className="fas fa-spinner fa-spin"></i>
            Uploading...
          </>
        ) : (
          <>
            <i className="fal fa-upload"></i>
            Upload
          </>
        )}
      </FileRowButton>

      <FileRowInput
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        onChange={handleFileChange}
      />
    </FileRowContainer>
  );
}

export default React.memo(FileUploadRow);
